<template>
	<v-menu v-model="menuOpen" location="end" :close-on-content-click="false">
		<template v-slot:activator="{ props }">
			<v-list-item v-bind="props" title="Load Spots"
				prependIcon="mdi-cloud-download" appendIcon="mdi-menu-right">
			</v-list-item>
		</template>
		<v-list>
			<v-list-item title="Current Hour" @click="$emit('loadSpotsCurrentHour')"/>
			<v-list-item title="Current Day" @click="$emit('loadSpotsCurrentDay')"/>
			<v-list-item title="Custom Range"
				:append-icon="`mdi-menu-${customRangeOpen ? 'down' : 'right'}`">
				<v-menu v-model="customRangeOpen" activator="parent" :close-on-content-click="false">
					<v-list>
						<v-form v-model="isValid" ref="loadSpotsForm" @submit.prevent="loadSpotsCustomRange">
							<v-list-item class="d-flex flex-row flex-nowrap">
								<v-row>
									<v-col cols="3" class="align-content-center">
										<v-list-item-title width>From:</v-list-item-title>
									</v-col>
									<v-col cols="5">
										<v-menu v-model="datePickerFromOpen" :close-on-content-click="false">
											<template v-slot:activator="{ props }">
												<v-text-field v-bind="props" class="mr-8"
													:model-value="fromDate.format('YYYY-MM-DD')"
													:rules="[loadSpotsValidationFrom]"
													append-inner-icon="mdi-calendar"
													variant="outlined"
													density="compact"
													flat
													single-line
													hide-details
													width="105"
												/>
											</template>
											<v-date-picker
												v-model="fromDate"
												color="secondary"
												border="sm"
												elevation="6"
												hide-header
												show-adjacent-months
												@update:model-value="validateForm"/>
										</v-menu>
									</v-col>
									<v-col cols="4">
										<v-menu v-model="timePickerFromOpen" :close-on-content-click="false">
											<template v-slot:activator="{ props }">
												<v-text-field v-bind="props"
													:model-value="fromTime"
													:rules="[loadSpotsValidationFrom]"
													append-inner-icon="mdi-clock-time-four-outline"
													variant="outlined"
													density="compact"
													flat
													single-line
													hide-details
													width="80"
													readonly/>
											</template>
											<v-time-picker
												v-model="fromTime"
												hide-header
												format="24hr"
												border="sm"
												elevation="6"
												:allowed-minutes="[0]"
												@update:model-value="validateForm"
											/>
										</v-menu>
									</v-col>
								</v-row>
								<v-row v-if="!isValid" class="mt-0">
									<v-spacer/>
									<v-col cols="9" class="text-body-2 text-error pt-0">
										{{ loadSpotsValidationFrom() }}
									</v-col>
								</v-row>
							</v-list-item>
							<v-list-item>
								<v-row>
									<v-col cols="3" class="align-content-center">
										<v-list-item-title>To:</v-list-item-title>
									</v-col>
									<v-col cols="5">
										<v-menu v-model="datePickerToOpen" :close-on-content-click="false">
											<template v-slot:activator="{ props }">
												<v-text-field v-bind="props" class="mr-8"
													:model-value="toDate.format('YYYY-MM-DD')"
													:rules="[loadSpotsValidationTo]"
													append-inner-icon="mdi-calendar"
													variant="outlined"
													density="compact"
													flat
													single-line
													hide-details
													width="105"
												/>
											</template>
											<v-date-picker
												v-model="toDate"
												color="secondary"
												border="sm"
												elevation="6"
												hide-header
												show-adjacent-months
												@update:model-value="validateForm"/>
										</v-menu>
									</v-col>
									<v-col cols="4">
										<v-menu v-model="timePickerToOpen" :close-on-content-click="false">
											<template v-slot:activator="{ props }">
												<v-text-field v-bind="props"
													:model-value="toTime"
													:rules="[loadSpotsValidationTo]"
													append-inner-icon="mdi-clock-time-four-outline"
													variant="outlined"
													density="compact"
													flat
													single-line
													hide-details
													width="80"
													readonly/>
											</template>
											<v-time-picker
												v-model="toTime"
												hide-header
												format="24hr"
												border="sm"
												elevation="6"
												:allowed-minutes="[0]"
												@update:model-value="validateForm"
											/>
										</v-menu>
									</v-col>
								</v-row>
								<v-row v-if="!isValid" class="mt-0">
									<v-spacer/>
									<v-col cols="9" class="text-body-2 text-error pt-0">
										{{ loadSpotsValidationTo() }}
									</v-col>
								</v-row>
							</v-list-item>
							<v-list-item class="d-flex justify-center">
								<v-btn variant="text"
									color="primary"
									prepend-icon="mdi-check"
									type="submit"
								/>
								<v-btn variant="text"
									color="error"
									prepend-icon="mdi-close"
									@click="customRangeOpen = false"
								/>
							</v-list-item>
						</v-form>
					</v-list>
				</v-menu>
			</v-list-item>
		</v-list>
	</v-menu>
</template>

<script>
import moment from "moment-timezone";

export default {
	name: "PlaylistLoadSpotsMenuItem",

	emits: ["loadSpotsCurrentHour", "loadSpotsCurrentDay", "loadSpotsCustomRange"],

	props: ["stationDateTime"],

	data() {
		return {
			menuOpen: false,
			customRangeOpen: false,
			datePickerFromOpen: false,
			fromDate: this.stationDateTime.clone().startOf("hour"),
			timePickerFromOpen: false,
			fromTime: this.stationDateTime.clone().startOf("hour").format('HH:mm'),
			datePickerToOpen: false,
			toDate: this.stationDateTime.clone().add(1, "hour").startOf("hour"),
			timePickerToOpen: false,
			toTime: this.stationDateTime.clone().add(1, "hour").startOf("hour").format('HH:mm'),
			isValid: true
		}
	},

	methods: {
		validateForm() {
			this.$refs.loadSpotsForm.validate();
			this.datePickerFromOpen = false;
			this.timePickerFromOpen = false;
			this.datePickerToOpen = false;
			this.timePickerToOpen = false;
		},

		loadSpotsValidationFrom() {
			const from = moment(`${this.fromDate.format("YYYY-MM-DD")} ${this.fromTime}`);
			const to = moment(`${this.toDate.format("YYYY-MM-DD")} ${this.toTime}`);

			return from.isBefore(to) || "Start datetime cannot be after end date";
		},

		loadSpotsValidationTo() {
			const from = moment(`${this.fromDate.format("YYYY-MM-DD")} ${this.fromTime}`);
			const to = moment(`${this.toDate.format("YYYY-MM-DD")} ${this.toTime}`);

			return to.isAfter(from) || "End datetime cannot be before start date";
		},

		loadSpotsCustomRange() {
			if (this.$refs["loadSpotsForm"].validate()) {
				const from = moment(`${this.fromDate.format("YYYY-MM-DD")} ${this.fromTime}`);
				const to = moment(`${this.toDate.format("YYYY-MM-DD")} ${this.toTime}`);

				this.$emit("loadSpotsCustomRange", {from, to});
			}
		}
	}
};
</script>